<template>
  <div class="farm-center-map">
    <AeMap
      :opt="mapOptions"
      :layers="mapLayers"
      @marker-click="handleMarkerClick"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { AeMap } from '@aevue/components'
import { baseLayers } from '../composables/useMapLayers'

const { t } = useI18n()

const props = defineProps({
  farmCenters: {
    type: Array,
    default: () => []
  },
  selectedId: {
    type: [String, Number],
    default: null
  },
  height: {
    type: String,
    default: '500px'
  }
})

const emit = defineEmits(['farm-click'])

// Calculate map center based on farm centers
const mapCenter = computed(() => {
  if (props.farmCenters.length === 0) {
    return [42.5, 12.5] // Default to center of Italy
  }

  // If there's a selected farm, center on it
  if (props.selectedId) {
    const selected = props.farmCenters.find(fc =>
      (fc.id_farm_center || fc.id) === props.selectedId
    )
    if (selected && selected.lat != null && selected.lon != null) {
      return [selected.lat, selected.lon]
    }
  }



  // Otherwise, center on the average of all farms
  const validFarms = props.farmCenters.filter(fc => fc.lat != null && fc.lon != null)
  if (validFarms.length === 0) {
    return [42.5, 12.5]
  }

  const avgLat = validFarms.reduce((sum, fc) => sum + fc.lat, 0) / validFarms.length
  const avgLng = validFarms.reduce((sum, fc) => sum + fc.lon, 0) / validFarms.length
  return [avgLat, avgLng]
})

// Calculate appropriate zoom level
const mapZoom = computed(() => {
  const validFarms = props.farmCenters.filter(fc => fc.lat != null && fc.lon != null)
  if (validFarms.length === 0) return 6
  if (validFarms.length === 1) return 12
  return 8
})

// Map options
const mapOptions = computed(() => ({  
  zoom: mapZoom.value,
  height: props.height,
  baseLayers: baseLayers,
  layer_selector: true
}))

// Convert farm centers to marker layers for AeMap
const mapLayers = computed(() => {
  const viewDetailsLabel = t('farmCenterMap.viewDetails')

  //crea una featureCollection GeoJSON
  const features = props.farmCenters
    .filter(fc => fc.lat != null && fc.lon != null && fc.lat!=0 && fc.lon!=0)
    .map(fc => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [fc.lon, fc.lat]
      },
      properties: fc
    }));

  return [{
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: features
    },
    options: {      
      popup: true,
      
      popupContent: `
        <div class="farm-popup">
          <h4>{{name}}</h4>
          <p>{{place}}</p>
          <a href="#/poderi/{{id_farm_center}}" class="view-link">${viewDetailsLabel}</a>
        </div>
      `
    }
  }];
  // return props.farmCenters
  //   .filter(fc => fc.lat != null && fc.lon != null)
  //   .map(fc => ({
  //     type: 'geojson',
  //     lat: fc.lat,
  //     lng: fc.lon,
  //     options: {
  //       id: fc.id_farm_center || fc.id,
  //       label: fc.name,
  //       popup: `
  //         <div class="farm-popup">
  //           <h4>${fc.name}</h4>
  //           <p>${ fc.place || ''}</p>
  //         </div>
  //       `
  //     }
  //   }))
})

function handleMarkerClick(marker) {
  const farm = props.farmCenters.find(fc =>
    (fc.id_farm_center || fc.id) === marker.id
  )
  if (farm) {
    emit('farm-click', farm)
  }
}
</script>

<style scoped>
.farm-center-map {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

:deep(.farm-popup h4) {
  margin: 0 0 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #333;
}

:deep(.farm-popup p) {
  margin: 0;
  font-size: 0.875rem;
  color: #666;
}
</style>
